import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LockKeyhole,
  FileWarning,
  Phone,
  Banknote,
  ShieldCheck,
  ScrollText,
  CalendarClock,
  Wallet,
  BarChart3,
  Building2,
  Trophy,
  GraduationCap,
  CalendarRange,
  ClipboardList,
  Flag,
  HeartPulse,
  Wrench,
  ArrowRight,
} from "lucide-react";
import { Reveal, TrackCorner } from "../components/krida/primitives";
import { scrollToEl } from "../lib/smooth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLIDE_LABELS = [
  "Cover", "The Problem", "What Krida Does", "How It Works", "Who Uses It",
  "Proof", "Outcomes", "Tamil Nadu", "Union Schemes", "Close",
];

const SlideNo = ({ n, dark = false }) => (
  <span className={`hidden shrink-0 font-mono text-[10px] tracking-[0.3em] uppercase sm:inline ${dark ? "text-white/35" : "text-kn-navy/35"}`}>
    Slide {String(n).padStart(2, "0")} / 10
  </span>
);

const PrintLockup = ({ dark = false }) => (
  <span className="print-lockup items-center gap-3">
    <img src={dark ? "/logo-white-wm.png" : "/logo-dark-wm.png"} alt="CYRA" className="h-5 w-auto" />
    <span className={`h-5 w-px ${dark ? "bg-white/20" : "bg-kn-navy/15"}`} />
    <span className={`font-serif text-base italic ${dark ? "text-kn-cyan" : "text-kn-blue"}`}>Krida</span>
  </span>
);

const Eyebrow = ({ children, dark = false }) => (
  <p className={`flex items-center gap-4 font-mono text-[11px] tracking-[0.32em] uppercase ${dark ? "text-kn-cyan" : "text-kn-blue"}`}>
    <span className={`h-px w-10 ${dark ? "bg-kn-cyan/60" : "bg-kn-blue/40"}`} />
    {children}
  </p>
);

const ACTIONS = [
  { icon: ShieldCheck, title: "Verify", desc: "Every coach, official and staff member gets a verified identity: Krida Pass." },
  { icon: ScrollText, title: "Credential", desc: "Certificates, grades and expiries in one register. Lapsed credentials are blocked automatically." },
  { icon: CalendarClock, title: "Deploy", desc: "Book officials, roster coaches and staff venues in hours, with check-in on the day." },
  { icon: Wallet, title: "Pay", desc: "Attendance-linked settlement in days, with tax and statutory deductions handled." },
  { icon: BarChart3, title: "Report", desc: "Live dashboards and audit-ready exports for every district, centre and event." },
];

const STEPS = [
  { n: "01", t: "Verify", d: "Identity captured once" },
  { n: "02", t: "Credential", d: "Grades and expiries recorded" },
  { n: "03", t: "Deploy", d: "Matched and rostered to events" },
  { n: "04", t: "Pay", d: "Settled after check-in" },
  { n: "05", t: "Report", d: "Dashboards and audit export" },
];

const ORGS = [
  { icon: Building2, label: "Sports authorities and departments" },
  { icon: Trophy, label: "Federations and associations" },
  { icon: GraduationCap, label: "Academies and centres of excellence" },
  { icon: CalendarRange, label: "Event organisers and venues" },
];

const PROS = [
  { icon: ClipboardList, label: "Coaches and PE instructors" },
  { icon: Flag, label: "Referees, umpires and technical officials" },
  { icon: HeartPulse, label: "Physios, trainers and analysts" },
  { icon: Wrench, label: "Venue and event crews" },
];

const OUTCOMES = [
  ["Professionals with verified identity and credential", "90%", "38%"],
  ["Lapsed credentials reaching the field", "0 — blocked at booking", "Not tracked"],
  ["Time to fill an official's slot", "Under 24 hours", "5–7 days"],
  ["Event staffing fill rate / no-shows", "97% / 3%", "84% / 11%"],
  ["Payment after an event", "3 days, on record", "21–30 days, cash"],
  ["Payouts with tax and statutory trail", "100%", "0%"],
  ["Admin hours per event", "10 hours", "36 hours"],
];

const TN_ROWS = [
  ["10 Olympic Centres of Excellence", "Credential-checked coaches and physios, rostered and paid per centre"],
  ["Tamil Nadu Rural Games", "Officials and crews booked across districts, paid the same week"],
  ["Daily sports period in government schools", "Verified PE instructors deployed at scale, attendance evidenced"],
  ["STAMS and data-driven governance", "A workforce record beside the athlete record, via API"],
  ["Athlete livelihoods after competition", "A recognised pathway from athlete to coach or official"],
];

const SCHEMES = [
  { title: "Staffing for Khelo India centres", desc: "Verified coaches and support staff deployed and paid for district centres, accredited academies and feeder schools." },
  { title: "NCAB-aligned coach register", desc: "A state register that carries National Coach Accreditation Board grades and feeds the unified digital sports platform." },
  { title: "Placement-linked skilling", desc: "Rural youth trained and placed as coaches, officials and venue staff under DDU-GKY and Skill India, with the wage trail the schemes require." },
];

const DeckPage = () => {
  const rootRef = useRef(null);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("krida-deck-access") === "1");
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [gateError, setGateError] = useState("");
  const [active, setActive] = useState(0);
  const targetRef = useRef(0);
  const lastNavRef = useRef(0);

  const goTo = (i) => {
    targetRef.current = i;
    lastNavRef.current = Date.now();
    const sections = rootRef.current?.querySelectorAll(":scope > section");
    if (sections && sections[i]) scrollToEl(sections[i]);
  };

  const unlock = async (e) => {
    e.preventDefault();
    setChecking(true);
    setGateError("");
    try {
      await axios.post(`${API}/deck-access`, { passcode: code });
      sessionStorage.setItem("krida-deck-access", "1");
      setUnlocked(true);
    } catch (err) {
      setGateError(err.response?.status === 429 ? "Too many attempts. Try again shortly." : "Incorrect passcode.");
    } finally {
      setChecking(false);
    }
  };

  // scroll-spy over the 10 slide sections
  useEffect(() => {
    if (!unlocked) return;
    const sections = Array.from(rootRef.current?.querySelectorAll(":scope > section") || []);
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const i = sections.indexOf(en.target);
            setActive(i);
            // don't resync the nav target while a programmatic scroll is in flight
            if (Date.now() - lastNavRef.current > 1400) targetRef.current = i;
          }
        }),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [unlocked]);

  // arrow-key slide navigation
  useEffect(() => {
    if (!unlocked) return;
    const onKey = (e) => {
      if (["ArrowDown", "ArrowRight", "PageDown"].includes(e.key)) {
        e.preventDefault();
        goTo(Math.min(targetRef.current + 1, SLIDE_LABELS.length - 1));
      } else if (["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault();
        goTo(Math.max(targetRef.current - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [unlocked, active]);

  useEffect(() => {
    document.title = "CYRA Krida";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.title = "CYRA Krida — The Sports Workforce OS";
      document.head.removeChild(meta);
    };
  }, []);

  if (!unlocked) {
    return (
      <div className="noise relative flex min-h-screen items-center justify-center overflow-hidden bg-[#021224]" data-testid="deck-gate">
        <div className="mesh-grid pointer-events-none absolute inset-0 opacity-50" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(circle at 50% 30%, rgba(46,197,230,0.14) 0%, transparent 60%)" }}
        />
        <div className="relative w-full max-w-sm px-6">
          <div className="rounded-2xl border border-kn-cyan/20 bg-kn-navy/70 p-10 shadow-[0_40px_120px_-40px_rgba(2,18,36,0.9)] backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <img src="/logo-white-wm.png" alt="CYRA" className="h-7 w-auto" />
              <span className="h-6 w-px bg-white/15" />
              <span className="font-serif text-lg italic text-kn-cyan">Krida</span>
            </div>
            <p className="mt-6 flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-kn-cyan">
              <LockKeyhole className="h-3.5 w-3.5" strokeWidth={1.5} />
              Private
            </p>
            <p className="mt-4 text-sm leading-relaxed text-white/60">
              This deck is shared privately. Enter the passcode to continue.
            </p>
            <form onSubmit={unlock} className="mt-8">
              <input
                type="password"
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Passcode"
                data-testid="deck-passcode-input"
                className="w-full rounded-xl border border-kn-cyan/25 bg-kn-navy/60 px-4 py-3 text-sm text-white shadow-inner placeholder:text-white/30 transition-colors duration-300 focus:border-kn-cyan focus:outline-none"
              />
              {gateError && <p className="mt-3 text-sm text-red-400" data-testid="deck-gate-error">{gateError}</p>}
              <button
                type="submit"
                disabled={checking}
                data-testid="deck-gate-submit"
                className="mt-5 w-full rounded-full bg-brand-gradient py-3.5 text-sm font-semibold text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(46,197,230,0.3)] active:scale-[0.98] disabled:opacity-60"
              >
                {checking ? "Checking…" : "Open the deck"}
              </button>
            </form>
          </div>
          <p className="mt-6 text-center font-mono text-[10px] tracking-[0.24em] uppercase text-white/30">
            CYRA Krida · The Sports Workforce OS
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="deck-print bg-white" data-testid="deck-page">
      {/* slide navigation dots */}
      <nav
        className="deck-dots fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-2.5 rounded-full border border-kn-navy/10 bg-white/80 px-2 py-3 shadow-lg backdrop-blur-md md:flex"
        data-testid="deck-dots"
      >
        {SLIDE_LABELS.map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => goTo(i)}
            aria-label={label}
            data-testid={`deck-dot-${i}`}
            className="group relative flex items-center"
          >
            <span className="pointer-events-none absolute right-6 whitespace-nowrap rounded-full bg-[#04203F]/90 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white opacity-0 backdrop-blur transition-opacity duration-200 group-hover:opacity-100">
              {label}
            </span>
            <span
              className={`block w-1.5 rounded-full transition-all duration-300 ${
                active === i ? "h-5 bg-kn-blue" : "h-1.5 bg-kn-navy/25 group-hover:bg-kn-blue/50"
              }`}
            />
          </button>
        ))}
        <span className="mt-1 border-t border-kn-navy/10 pt-2 font-mono text-[9px] tracking-[0.12em] text-kn-navy/55" data-testid="deck-slide-counter">
          {String(active + 1).padStart(2, "0")}/{SLIDE_LABELS.length}
        </span>
      </nav>
      {/* slim deck bar */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-kn-cyan/15 bg-[#04203F]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="deck-home-link" className="flex items-center gap-3">
            <img src="/logo-white-wm.png" alt="CYRA" className="h-6 w-auto" />
            <span className="h-5 w-px bg-white/15" />
            <span className="font-serif text-lg italic text-kn-cyan">Krida</span>
          </Link>
          <a href="mailto:hello@krida.me" data-testid="deck-contact-link" className="font-mono text-[10px] tracking-[0.24em] uppercase text-kn-cyan transition-colors hover:text-white">
            hello@krida.me
          </a>
        </div>
      </header>

      {/* Slide 1 — Cover */}
      <section className="noise relative flex min-h-screen flex-col justify-center overflow-hidden bg-[#021224]" data-testid="deck-cover">
        <img
          src="/images/deck-cover.jpg"
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-30 saturate-[0.45]"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(2,18,36,0.82) 0%, rgba(2,18,36,0.68) 50%, #021224 100%)" }}
        />
        <div className="mesh-grid pointer-events-none absolute inset-0 opacity-60" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(circle at 50% 30%, rgba(46,197,230,0.16) 0%, rgba(15,116,196,0.07) 45%, transparent 70%)" }}
        />
        <div className="pointer-events-none absolute -right-24 -top-24 text-kn-cyan/15">
          <TrackCorner className="h-[420px] w-[420px] rotate-90" />
        </div>
        <div className="pointer-events-none absolute -bottom-32 -left-32 text-white/10">
          <TrackCorner className="h-[380px] w-[380px] -scale-y-100" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-36 text-center lg:px-10">
          <Reveal>
            <div className="flex items-center justify-center gap-4" data-testid="deck-cover-logo">
              <img src="/logo-white-wm.png" alt="CYRA" className="h-11 w-auto" />
              <span className="h-9 w-px bg-white/20" />
              <span className="font-serif text-3xl italic leading-none text-kn-cyan">Krida</span>
            </div>
            <p className="mt-6 font-mono text-[11px] tracking-[0.32em] uppercase text-kn-cyan/80">The Sports Workforce OS</p>
          </Reveal>
          <Reveal delay={0.15}>
            <h1 className="mx-auto mt-14 max-w-4xl font-serif text-4xl italic leading-[1.12] text-white sm:text-6xl" data-testid="deck-cover-headline">
              The people behind sport,{" "}
              <span className="text-brand-gradient">verified and paid.</span>
            </h1>
            <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-white/65">
              One platform to onboard, credential, deploy and pay coaches, officials and event staff.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mx-auto mt-16 flex max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-white/10 pt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">
              <span>CYRA Works</span>
              <span className="h-1 w-1 rounded-full bg-kn-cyan/50" />
              <span>India</span>
              <span className="h-1 w-1 rounded-full bg-kn-cyan/50" />
              <span>Singapore</span>
              <span className="h-1 w-1 rounded-full bg-kn-cyan/50" />
              <span>Malaysia</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Slide 2 — The Problem */}
      <section className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="deck-problem">
        <img src="/images/deck-shuttle.jpg" alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover saturate-[0.5]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.86) 55%, rgba(255,255,255,0.95) 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow>The Problem</Eyebrow>
              <PrintLockup />
              <SlideNo n={2} />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-kn-navy sm:text-5xl">
              Every sports programme runs on people{" "}
              <span className="font-serif italic font-normal text-brand-gradient">nobody is tracking.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 lg:grid-cols-3">
            {[
              { icon: FileWarning, title: "Unverified", desc: "Coach and official credentials live in paper files and WhatsApp groups." },
              { icon: Phone, title: "Hired by phone", desc: "Referees and event crews are booked by call. No-shows and double bookings go unmeasured." },
              { icon: Banknote, title: "Paid in cash, late", desc: "Match fees settle weeks after the event with no tax or audit trail." },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="h-full">
                <div className="group flex h-full flex-col gap-6 bg-white p-8 transition-colors duration-500 hover:bg-kn-navy" data-testid={`deck-problem-${i}`}>
                  <p.icon className="h-8 w-8 text-kn-blue transition-colors duration-500 group-hover:text-kn-cyan" strokeWidth={1} />
                  <div>
                    <h3 className="text-lg font-semibold text-kn-navy transition-colors duration-500 group-hover:text-white">{p.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-kn-navy/60 transition-colors duration-500 group-hover:text-white/60">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Slide 3 — What Krida Does */}
      <section className="relative overflow-hidden bg-kn-ice py-24 lg:py-32" data-testid="deck-what">
        <img src="/images/deck-pool.jpg" alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover saturate-[0.5]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(238,246,252,0.93) 0%, rgba(238,246,252,0.87) 50%, rgba(238,246,252,0.95) 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow>What Krida Does</Eyebrow>
              <PrintLockup />
              <SlideNo n={3} />
            </div>
            <h2 className="mt-8 max-w-2xl text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
              What Krida does for you
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 sm:grid-cols-3 lg:grid-cols-5">
            {ACTIONS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06} className="h-full">
                <div className="group flex h-full flex-col justify-between gap-8 bg-white p-6 transition-colors duration-500 hover:bg-kn-navy" data-testid={`deck-action-${i}`}>
                  <div className="flex items-center justify-between">
                    <a.icon className="h-6 w-6 text-kn-blue transition-colors duration-500 group-hover:text-kn-cyan" strokeWidth={1.25} />
                    <span className="font-mono text-[10px] text-kn-navy/30 transition-colors duration-500 group-hover:text-white/40">0{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-kn-navy transition-colors duration-500 group-hover:text-white">{a.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-kn-navy/60 transition-colors duration-500 group-hover:text-white/60">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-14 border-l-2 border-kn-cyan pl-6 font-serif text-2xl italic leading-snug text-kn-navy2">
              Built for the workforce, not the athlete. Athletes appear only as participants.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Slide 4 — How It Works */}
      <section className="noise relative overflow-hidden bg-kn-navy py-24 lg:py-32" data-testid="deck-how">
        <img
          src="/images/deck-swimmer.jpg"
          alt=""
          loading="lazy"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12] saturate-[0.4]"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 500px at 80% 20%, rgba(15,116,196,0.2), transparent 60%), linear-gradient(180deg, rgba(4,32,63,0.55) 0%, rgba(4,32,63,0.85) 100%)" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow dark>How It Works</Eyebrow>
              <PrintLockup dark />
              <SlideNo n={4} dark />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-white sm:text-5xl">
              From an unknown name to a paid professional in{" "}
              <span className="font-serif italic font-normal text-brand-gradient">five steps.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.07} className="h-full">
                <div className="group flex h-full flex-col justify-between gap-5 bg-kn-navy p-6 transition-colors duration-500 hover:bg-kn-navy2 lg:gap-10" data-testid={`deck-step-${i}`}>
                  <span className="font-mono text-xs text-kn-cyan">{s.n}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{s.t}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-white/55 lg:min-h-[42px]">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Slide 5 — Who Uses It */}
      <section className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="deck-who">
        <img src="/images/deck-basketball.jpg" alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover saturate-[0.5]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.86) 50%, rgba(255,255,255,0.95) 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow>Who Uses It</Eyebrow>
              <PrintLockup />
              <SlideNo n={5} />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-kn-navy sm:text-5xl">
              Built for the organisations that run sport and the{" "}
              <span className="font-serif italic font-normal text-brand-gradient">professionals who work in it.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:gap-20">
            {[
              { title: "Organisations", items: ORGS, tid: "deck-orgs" },
              { title: "Professionals", items: PROS, tid: "deck-pros" },
            ].map((col) => (
              <Reveal key={col.title} delay={0.1}>
                <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-kn-blue">{col.title}</p>
                <div className="mt-6 divide-y divide-kn-navy/10 border-y border-kn-navy/10" data-testid={col.tid}>
                  {col.items.map((it, i) => (
                    <div key={it.label} className="flex items-center gap-4 py-4">
                      <it.icon className="h-5 w-5 shrink-0 text-kn-blue" strokeWidth={1.25} />
                      <span className="text-sm font-medium text-kn-navy">{it.label}</span>
                      <span className="ml-auto font-mono text-[10px] text-kn-navy/30">0{i + 1}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Slide 6 — Proof */}
      <section className="noise relative overflow-hidden bg-[#021224] py-24 lg:py-32" data-testid="deck-proof">
        <img
          src="/images/deck-badminton.jpg"
          alt=""
          loading="lazy"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12] saturate-[0.4]"
        />
        <div className="mesh-grid pointer-events-none absolute inset-0 opacity-40" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(circle at 20% 20%, rgba(46,197,230,0.12) 0%, transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow dark>Proof</Eyebrow>
              <PrintLockup dark />
              <SlideNo n={6} dark />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-white sm:text-5xl">
              Live pilot with the{" "}
              <span className="font-serif italic font-normal text-brand-gradient">Badminton Association of Malaysia.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 lg:grid-cols-4" data-testid="deck-proof-stats">
              {[
                ["480", "coaches and officials across three state associations"],
                ["12", "tournaments staffed, Sep 2026 – Jan 2027"],
                ["1,900+", "match-fee settlements on the platform"],
                ["3", "state associations live"],
              ].map(([v, l], i) => (
                <div key={l} className="bg-[#021224] p-8" data-testid={`deck-proof-stat-${i}`}>
                  <p className="text-4xl font-semibold text-white">{v}</p>
                  <p className="mt-3 text-[13px] leading-relaxed text-white/55">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <div className="mt-6 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 lg:grid-cols-3">
            {[
              ["24 hrs", "to fill an official's slot", "from 5–7 days"],
              ["3 days", "event close to payment", "from 21–30 days"],
              ["97%", "staffing fill rate", "up from 84%"],
            ].map(([v, l, d], i) => (
              <Reveal key={l} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col gap-3 bg-[#04203F] p-8" data-testid={`deck-proof-metric-${i}`}>
                  <p className="text-4xl font-semibold text-kn-cyan">{v}</p>
                  <p className="text-sm font-medium text-white">{l}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Slide 7 — Measurable Outcomes */}
      <section className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="deck-outcomes">
        <img src="/images/deck-court.jpg" alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover saturate-[0.5]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.86) 50%, rgba(255,255,255,0.95) 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow>Measurable Outcomes</Eyebrow>
              <PrintLockup />
              <SlideNo n={7} />
            </div>
            <h2 className="mt-8 max-w-2xl text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
              What changes in the first six months
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-14 overflow-hidden rounded-2xl border border-kn-navy/10" data-testid="deck-outcomes-table">
              <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-4 border-b border-kn-navy/10 bg-kn-ice px-6 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-kn-navy/50 sm:px-8">
                <span>Outcome</span>
                <span className="text-kn-blue">With Krida</span>
                <span>Today</span>
              </div>
              <div className="divide-y divide-kn-navy/[0.07]">
                {OUTCOMES.map(([o, k, t], i) => (
                  <div key={o} className="grid grid-cols-[1.4fr_1fr_1fr] items-center gap-4 px-6 py-4 sm:px-8" data-testid={`deck-outcome-${i}`}>
                    <span className="text-[13px] font-medium text-kn-navy/80">{o}</span>
                    <span className="text-sm font-semibold text-kn-blue">{k}</span>
                    <span className="text-[13px] text-kn-navy/45">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-6 max-w-2xl text-[13px] leading-relaxed text-kn-navy/50">
              Targets from the BAM pilot design. Baselines are re-measured in the first two weeks, so every gain is yours to verify.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Slide 8 — For Tamil Nadu */}
      <section className="relative overflow-hidden bg-kn-ice py-24 lg:py-32" data-testid="deck-tn">
        <img src="/images/deck-wrestlers.jpg" alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover saturate-[0.5]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(238,246,252,0.93) 0%, rgba(238,246,252,0.87) 50%, rgba(238,246,252,0.95) 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow>For Tamil Nadu</Eyebrow>
              <PrintLockup />
              <SlideNo n={8} />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-kn-navy sm:text-5xl">
              What Krida delivers against the{" "}
              <span className="font-serif italic font-normal text-brand-gradient">state's sports priorities.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-14 divide-y divide-kn-navy/10 border-y border-kn-navy/10" data-testid="deck-tn-rows">
              {TN_ROWS.map(([p, d], i) => (
                <div key={p} className="grid items-start gap-2 py-6 sm:grid-cols-2 sm:gap-10" data-testid={`deck-tn-row-${i}`}>
                  <div className="flex items-start gap-4">
                    <span className="pt-1 font-mono text-xs text-kn-blue">0{i + 1}</span>
                    <h3 className="text-base font-semibold text-kn-navy">{p}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-kn-navy/60">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Slide 9 — Union Schemes */}
      <section className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="deck-schemes">
        <img src="/images/deck-basketball-2.jpg" alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover saturate-[0.5]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.86) 50%, rgba(255,255,255,0.95) 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow>Union Schemes</Eyebrow>
              <PrintLockup />
              <SlideNo n={9} />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-kn-navy sm:text-5xl">
              What Krida can deliver under{" "}
              <span className="font-serif italic font-normal text-brand-gradient">Khelo India 2026–31.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 lg:grid-cols-3">
            {SCHEMES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="h-full">
                <div className="group flex h-full flex-col justify-between gap-10 bg-white p-8 transition-colors duration-500 hover:bg-kn-navy" data-testid={`deck-scheme-${i}`}>
                  <span className="font-mono text-xs text-kn-blue transition-colors duration-500 group-hover:text-kn-cyan">0{i + 1}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-kn-navy transition-colors duration-500 group-hover:text-white">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-kn-navy/60 transition-colors duration-500 group-hover:text-white/60">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Slide 10 — Closing */}
      <section className="noise relative overflow-hidden bg-[#021224] py-24 lg:py-32" data-testid="deck-closing">
        <img
          src="/images/deck-pool-2.jpg"
          alt=""
          loading="lazy"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.18] saturate-[0.4]"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(2,18,36,0.7) 0%, rgba(2,18,36,0.55) 50%, #021224 100%)" }}
        />
        <div className="mesh-grid pointer-events-none absolute inset-0 opacity-50" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 500px at 50% 100%, rgba(46,197,230,0.14), transparent 60%)" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center justify-between">
              <Eyebrow dark>Let's Build It Together</Eyebrow>
              <PrintLockup dark />
              <SlideNo n={10} dark />
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[1.12] tracking-tight text-white sm:text-5xl">
              Where would you{" "}
              <span className="font-serif italic font-normal text-brand-gradient">like to start?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col gap-8 border-t border-white/10 pt-10 lg:flex-row lg:items-center lg:justify-between">
              <p className="max-w-md text-sm leading-relaxed text-white/60">
                Next step: a working session to shape the pilot around your priorities.
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[11px] tracking-[0.2em] uppercase text-white/50" data-testid="deck-contact-block">
                <span className="text-white">Guhan · Founder & CEO, CYRA Works</span>
                <a href="mailto:hello@krida.me" className="flex items-center gap-2 text-kn-cyan transition-colors hover:text-white" data-testid="deck-email-link">
                  hello@krida.me <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
                <Link to="/" className="flex items-center gap-2 transition-colors hover:text-kn-cyan" data-testid="deck-site-link">
                  krida.me <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default DeckPage;
